import {createSelector, Dictionary} from '@reduxjs/toolkit';
import parseISO from 'date-fns/parseISO';
import isSameDay from 'date-fns/isSameDay';
import createCachedSelector from 're-reselect';
import {
  getDefaultBuilding,
  getDefaultBuildingId,
  getBuildingById,
  getBuildingEntities,
  getAllBuildings,
  getTotalBuildings,
} from '../../data/buildings';
import {RootState} from '../../rootReducer';
import {DeskListItem, GetListOfSelectableWorkingSpacesProps, NodeAvailabilityType, SelectableWorkspace} from './types';
import {
  getNodeInformationByNodeId,
  getNodeInformationByNodeIdInEditMode,
  getNodeInformationForAllNodes,
  nodeNotFoundResult,
} from '../nodeInformation';
import {
  getIsLastSelectedWorkspaceReservationLocationLoaded,
  getWorkspaceReservationByDate,
  getIsDeletingRegistration,
  getWorkspaceReservationById,
  getIsCreatingWorkspaceReservation,
  getIsUpdatingWorkspaceReservationById,
  getIsDeletingWorkspaceReservationById,
  getLastSelectedWorkspaceReservationLocation,
} from '../../data/workspaceReservation';
import {getAreaById, getAreaEntities} from '../../data/areas';
import {getFloorById, getFloorEntities, getFloorsByBuildingId} from '../../data/floors';
import {
  getDeskById,
  getIsLoadingDeskBookingStatus,
  getIsLoadingDeskSuggestions,
  getSuggestionError,
} from '../../data/desks';
import {byProp, getStartOfDay, isBeforeToday, numericCompareAsc, stringCompare} from '@lib/utils';
import {
  getAllWorkspaceReservationsForConnections,
  getConnectedUserEntities,
  getWorkspaceReservationsForConnectionsByDate,
} from '../../data/connections';
import {getIsLoadingParkingLots} from '../../data/parkingLot';
import {getUserCheckInStatus} from '../../data/checkIn';
import {getSettings, getStartOfTodayISOString} from '../../data/settings';
import {getDeskIdsOfWorkspaceReservation, getSpaceNodeTypeAndId} from './helpers';
import {getIsLoadingParkingCount} from '../../data/parkingReservation';
import {LocationNode, GetBuildingLocationsProps, BuildingLocation} from './types';
import {Area, Building, Floor, WorkspaceReservation} from '../../data/entities';
import {compareWorkdayBySortingOrder} from '../../data/workday/helpers';
import {getWorkdayByDate} from '../../data/workday';
import {getIsDayAllowed, getIsSelectedDateFuture} from '../../data/profiles';
import {getWeekQuotaByWeekNumber} from '../calendar';
import {getISOWeek} from 'date-fns';
import {getConnectedUsersWithReservationInNode, groupSelectableOfficeNodesByType} from './utils';
import {cachedDate} from '../../util';

export const getSelectedBuildingNodeId = (state: RootState) => state.ui.workspaceReservation.buildingId;

export const getSelectedFloorNodeId = (state: RootState) => state.ui.workspaceReservation.floorId;

export const getSelectedAreaNodeId = (state: RootState) => state.ui.workspaceReservation.areaId;

export const getSelectedDeskNodeIds = (state: RootState) => state.ui.workspaceReservation.deskIds;

export const getSelectedGroupWorkspaceInvitees = (state: RootState) => state.ui.workspaceReservation.invitees;

export const getIsSavingRegistration = (state: RootState) => state.ui.workspaceReservation.isSavingWorkspaceReservation;

export const getSelectedNodes = createSelector(
  getSelectedBuildingNodeId,
  getSelectedFloorNodeId,
  getSelectedAreaNodeId,
  getSelectedDeskNodeIds,
  (buildingId, floorId, areaId, deskIds) => ({buildingId, floorId, areaId, deskIds}),
);

const getNodeTypeAndIdOfLastSelectedWorkspaceReservation = createSelector(
  getLastSelectedWorkspaceReservationLocation,
  (lastSelectedWorkspaceReservationLocation) => {
    const {buildingId, floorId, areaId} = lastSelectedWorkspaceReservationLocation;
    return getSpaceNodeTypeAndId(buildingId, floorId, areaId);
  },
);

export const getSelectedNodeIdsWithInitialValue = createSelector(
  getSelectedBuildingNodeId,
  getSelectedFloorNodeId,
  getSelectedAreaNodeId,
  getSelectedDeskNodeIds,
  getDefaultBuilding,
  (state: RootState, selectedDate: string) =>
    getNodeInformationByNodeId(state, {nodeId: getDefaultBuildingId(state), date: parseISO(selectedDate)}),
  (state: RootState, selectedDate: string) =>
    getNodeInformationByNodeId(state, {
      nodeId: getNodeTypeAndIdOfLastSelectedWorkspaceReservation(state).spaceNodeId,
      date: parseISO(selectedDate),
    }),
  (state: RootState) => getBuildingById(state, getLastSelectedWorkspaceReservationLocation(state).buildingId),
  getLastSelectedWorkspaceReservationLocation,
  (state: RootState, selectedDate: string) => getWorkspaceReservationByDate(state, parseISO(selectedDate)),
  getUserCheckInStatus,
  (state: RootState) => getSelectedGroupWorkspaceInvitees(state).length,
  (state: RootState, selectedDate: string) =>
    getNodeInformationByNodeId(state, {nodeId: getUserCheckInStatus(state).buildingId, date: parseISO(selectedDate)}),
  (state: RootState, selectedDate: string) => getWorkdayByDate(state, selectedDate),
  getStartOfTodayISOString,
  (__: RootState, selectedDate: string) => selectedDate,
  (
    selectedBuildingId,
    selectedFloorId,
    selectedAreaId,
    selectedDeskIds,
    defaultBuilding,
    {isClosed: isDefaultBuildingClosed, isFull: isDefaultBuildingFull},
    {isClosed: isLastSelectedNodeClosed, isFull: isLastSelectedNodeFull},
    lastSelectedBuilding,
    {deskId: lastSelectedDeskId, areaId: lastSelectedAreaId, floorId: lastSelectedFloorId},
    workspaceReservation,
    {buildingId: checkInBuildingId, eventType},
    numberOfInvitees,
    {isClosed: isCheckInBuildingClosed, isFull: isCheckInBuildingFull},
    workday,
    todayISOString,
    selectedDate,
  ) => {
    const isToday = isSameDay(parseISO(selectedDate), parseISO(todayISOString));

    if (selectedBuildingId) {
      return {
        buildingId: selectedBuildingId,
        floorId: selectedFloorId,
        areaId: selectedAreaId,
        deskIds: selectedDeskIds,
      };
    }
    if (
      eventType === 'AtTheOffice' &&
      checkInBuildingId &&
      checkInBuildingId != workspaceReservation?.buildingId &&
      isToday &&
      !isCheckInBuildingClosed &&
      !isCheckInBuildingFull &&
      !numberOfInvitees
    ) {
      return {buildingId: checkInBuildingId};
    }
    if (workspaceReservation && !numberOfInvitees) {
      return {
        buildingId: workspaceReservation.buildingId,
        floorId: workspaceReservation.floorId,
        areaId: workspaceReservation.areaId,
        deskIds: getDeskIdsOfWorkspaceReservation(workspaceReservation),
      };
    }
    if (
      lastSelectedBuilding &&
      workday?.nodeId === lastSelectedBuilding.id &&
      lastSelectedBuilding.capacity > 0 &&
      !isLastSelectedNodeClosed &&
      !isLastSelectedNodeFull &&
      !numberOfInvitees
    ) {
      return {
        buildingId: lastSelectedBuilding.id,
        floorId: lastSelectedFloorId,
        areaId: lastSelectedAreaId,
        deskIds: lastSelectedDeskId ? [lastSelectedDeskId] : [],
      };
    }
    if (
      defaultBuilding &&
      defaultBuilding.capacity > 0 &&
      !isDefaultBuildingClosed &&
      !isDefaultBuildingFull &&
      !numberOfInvitees
    ) {
      return {buildingId: defaultBuilding.id};
    }
  },
);

export const getInitialSelectedWorkspace = createCachedSelector(
  (state: RootState, {selectedDate}: {selectedDate: string}) =>
    getNodeInformationByNodeId(state, {
      nodeId: getNodeTypeAndIdOfLastSelectedWorkspaceReservation(state).spaceNodeId,
      date: parseISO(selectedDate),
    }),
  (state: RootState) => getBuildingById(state, getLastSelectedWorkspaceReservationLocation(state).buildingId),
  getLastSelectedWorkspaceReservationLocation,
  (state: RootState, {selectedDate}: {selectedDate: string}) =>
    getWorkspaceReservationByDate(state, parseISO(selectedDate)),
  (state: RootState, {selectedDate}: {selectedDate: string}) => getWorkdayByDate(state, selectedDate),
  (__: RootState, {buildingId}: {selectedDate: string; buildingId?: string}) => buildingId,
  (
    {isClosed: isLastSelectedNodeClosed, isFull: isLastSelectedNodeFull},
    lastSelectedBuilding,
    {deskId: lastSelectedDeskId, areaId: lastSelectedAreaId, floorId: lastSelectedFloorId},
    workspaceReservation,
    workday,
    deskMandatoryBuildingId,
  ) => {
    const buildingId = deskMandatoryBuildingId || workday?.nodeId || '';

    if (workspaceReservation && !deskMandatoryBuildingId) {
      return {
        buildingId: workspaceReservation.buildingId,
        floorId: workspaceReservation.floorId,
        areaId: workspaceReservation.areaId,
        deskIds: getDeskIdsOfWorkspaceReservation(workspaceReservation),
      };
    }

    if (
      buildingId &&
      buildingId === lastSelectedBuilding?.id &&
      lastSelectedBuilding.capacity > 0 &&
      !isLastSelectedNodeClosed &&
      !isLastSelectedNodeFull
    ) {
      return {
        buildingId: lastSelectedBuilding.id,
        floorId: lastSelectedFloorId,
        areaId: lastSelectedAreaId,
        deskIds: lastSelectedDeskId ? [lastSelectedDeskId] : [],
      };
    }

    if (buildingId) {
      return {
        buildingId: buildingId,
      };
    }
  },
)((_state, {selectedDate, buildingId}) => `${selectedDate}:${buildingId || ''}`);

export const getIsSelectedBuildingWithoutFloors = createSelector(
  (state: RootState) => getFloorsByBuildingId(state, getSelectedBuildingNodeId(state)),
  (floors) => {
    return floors.length === 0;
  },
);

export const getLastSelectedNodeId = createSelector(
  getLastSelectedWorkspaceReservationLocation,
  ({deskId, areaId, floorId, buildingId}) => {
    if (deskId) {
      return deskId;
    }
    if (areaId) {
      return areaId;
    }
    if (floorId) {
      return floorId;
    }
    return buildingId;
  },
);

export const getSelectedSectionTitle = createSelector(
  (state: RootState) => getAreaById(state, getSelectedAreaNodeId(state)),
  (state: RootState) => getFloorById(state, getSelectedFloorNodeId(state)),
  (state: RootState, selectedDate: string) =>
    getNodeInformationByNodeIdInEditMode(state, {nodeId: getSelectedFloorNodeId(state), date: parseISO(selectedDate)}),
  (state: RootState, selectedDate: string) =>
    getNodeInformationByNodeIdInEditMode(state, {nodeId: getSelectedAreaNodeId(state), date: parseISO(selectedDate)}),
  (
    selectedArea,
    selectedFloor,
    {isClosed: isClosedFloor, isFull: isFullFloor},
    {isClosed: isClosedArea, isFull: isFullArea},
  ) => {
    if (selectedArea && !isClosedArea && !isFullArea) {
      return selectedArea.name;
    }
    if (selectedFloor && !isClosedFloor && !isFullFloor) {
      return selectedFloor.name;
    }
  },
);

export const getSelectedSectionSubtitle = createSelector(
  (state: RootState) => getDeskById(state, getSelectedDeskNodeIds(state)[0]),
  (state: RootState) => getAreaById(state, getSelectedAreaNodeId(state)),
  (state: RootState) => getFloorById(state, getSelectedFloorNodeId(state)),
  (_state: RootState, anyAvailableDesk: string) => anyAvailableDesk,
  (selectedDesk, selectedArea, selectedFloor, anyAvailableDesk) => {
    if (selectedArea && selectedFloor && selectedDesk) {
      return selectedFloor.name;
    }
    if (selectedArea && selectedFloor && !selectedDesk) {
      return `${selectedFloor.name}, ${anyAvailableDesk.toLocaleLowerCase()}`;
    }
    if (selectedFloor && !selectedDesk) {
      return anyAvailableDesk;
    }
  },
);

export const getCanSelectWorkspace = createSelector(
  getSelectedBuildingNodeId,
  getSelectedNodeIdsWithInitialValue,
  getIsLastSelectedWorkspaceReservationLocationLoaded,
  (selectedBuildingId, selectedNodeIdsWithInitialValue, isLastSelectedLocationLoaded) => {
    return !selectedBuildingId && selectedNodeIdsWithInitialValue && isLastSelectedLocationLoaded;
  },
);

export const getListOfSelectableWorkingSpaces = createCachedSelector(
  (state: RootState, {buildingId}: GetListOfSelectableWorkingSpacesProps) => getFloorsByBuildingId(state, buildingId),
  getAreaEntities,
  getAllWorkspaceReservationsForConnections,
  getConnectedUserEntities,
  (__: RootState, {date}) => date,
  (floors, allAreas, workspaceReservations, connectedUserEntities, date) => {
    const workspaceReservationsForSameDay = workspaceReservations.filter(
      (workspaceReservation) =>
        isSameDay(getStartOfDay(workspaceReservation.startDate), getStartOfDay(date)) &&
        workspaceReservation.status === 'Confirmed',
    );

    const result = floors
      .filter((floor) => floor.capacity > 0)
      .flatMap((floor) => {
        const areas = floor.areas;

        if (!areas || !areas.length) {
          const connectedUsersFloors = getConnectedUsersWithReservationInNode(
            workspaceReservationsForSameDay,
            connectedUserEntities,
            floor,
          );

          return {
            areaId: undefined,
            subtitle: '',
            floorId: floor.id,
            title: floor.name,
            hasBookableDesks: false,
            connectedUsers: connectedUsersFloors,
            nodeId: floor.id,
            deskAmenities: [],
            activityTypes: [],
            workspaceBookingPolicyType: undefined,
          };
        }

        return areas.reduce<SelectableWorkspace[]>((acc, curr) => {
          const area = allAreas[curr];
          if (
            !area ||
            !(area.workspaceBookingPolicyType === 'NonBookable' ? area.adHocCapacity > 0 : area.capacity > 0)
          )
            return acc;

          const connectedUsersAreas = getConnectedUsersWithReservationInNode(
            workspaceReservationsForSameDay,
            connectedUserEntities,
            area,
          );

          const newItem = {
            areaId: area?.id,
            title: area?.name ?? '',
            floorId: floor.id,
            subtitle: floor.name,
            hasBookableDesks: area?.hasBookableDesks ?? false,
            connectedUsers: connectedUsersAreas,
            nodeId: area?.id,
            deskAmenities: area?.deskAmenities || [],
            activityTypes: area?.activityTypes || [],
            workspaceBookingPolicyType: area?.workspaceBookingPolicyType,
          };

          acc.push(newItem);
          return acc;
        }, []);
      });

    return result.sort(
      (a, b) =>
        b.connectedUsers.length - a.connectedUsers.length || (a.title > b.title ? 1 : a.title < b.title ? -1 : 0),
    );
  },
)((_state, {buildingId, date}) => `${buildingId}:${date}`);

export const getListOfSelectableWorkingSpacesWithGroupType = createCachedSelector(
  getListOfSelectableWorkingSpaces,
  (state: RootState, {date, inviteeCount}) => ({
    state,
    date,
    inviteeCount,
  }),
  (listOfSelectableWorkingSpaces, {state, date, inviteeCount}) => {
    const selectedDate = parseISO(date);
    const result = listOfSelectableWorkingSpaces.map((workspace) => {
      const nodeId = workspace.nodeId;
      const nodeInformation = getNodeInformationByNodeIdInEditMode(state, {nodeId, date: selectedDate, inviteeCount});

      const {isFull, isClosed, hasInsufficientSpaceForGroupBooking} = nodeInformation;

      return {
        ...workspace,
        capacity: 1,
        nodeAvailability: (isFull || isClosed || hasInsufficientSpaceForGroupBooking
          ? 'Unavailable'
          : 'Available') as NodeAvailabilityType,
      };
    });
    return result;
  },
)((_state, {buildingId, date}) => `${buildingId}:${date}`);

export const getListOfSelectableWorkingSpacesByTypeNew = createCachedSelector(
  getListOfSelectableWorkingSpacesWithGroupType,
  (listOfSelectableWorkingSpacesWithGroupType) => {
    const init: {[key in NodeAvailabilityType]: DeskListItem[]} = {Available: [], Unavailable: []};

    return listOfSelectableWorkingSpacesWithGroupType.reduce((acc, curr) => {
      const currentType = curr.nodeAvailability;
      const valuesForCurrentType = acc[curr.nodeAvailability];
      return {
        ...acc,
        [currentType]: [...valuesForCurrentType, curr],
      };
    }, init);
  },
)((_state, {buildingId}) => buildingId || '');

export const getSortedListOfFloorsByName = createSelector(
  (state: RootState) => getFloorsByBuildingId(state, getSelectedBuildingNodeId(state)),
  (floors) => floors.sort(byProp('name')),
);

export const getWorkspaceQuotaInfo = createCachedSelector(
  getStartOfTodayISOString,
  getIsSelectedDateFuture,
  (state: RootState, date) => ({...getWeekQuotaByWeekNumber(state, getISOWeek(date)), date}),
  getIsDayAllowed,
  (todayISOString, isSelectedDateFuture, {total, count, date}, isDayAllowed) => ({
    isSelectedDateInPast: isBeforeToday(date, parseISO(todayISOString)),
    isSelectedDateFuture,
    quotaReached: count >= total,
    isDayAllowed,
  }),
)((_state, date) => `${date}`);

export const getIsOfficeDayBookableBasedOnQuota = createCachedSelector(getWorkspaceQuotaInfo, (quotaInfo) => {
  const {
    isSelectedDateInPast,
    isSelectedDateFuture, // 'Future' meaning any date beyond the `daysAhead`
    quotaReached,
    isDayAllowed,
  } = quotaInfo;

  return !isSelectedDateInPast && !isSelectedDateFuture && !quotaReached && isDayAllowed;
})((_state, date) => `${date}`);

export const getSelectableOfficeNodes = createCachedSelector(
  getAllBuildings,
  getUserCheckInStatus,
  getStartOfTodayISOString,
  getLastSelectedWorkspaceReservationLocation,
  getDefaultBuildingId,
  getWorkspaceQuotaInfo,
  getWorkspaceReservationByDate,
  getNodeInformationForAllNodes,
  (_state: RootState, date: Date) => cachedDate(date),
  (
    buildings,
    {buildingId: checkInBuildingId},
    todayISOString,
    lastSelectedWorkspaceReservationLocation,
    defaultBuildingId,
    {isSelectedDateFuture, quotaReached, isDayAllowed},
    workspaceReservation,
    allNodeInfos,
    date,
  ) => {
    const isToday = isSameDay(date!, parseISO(todayISOString));
    const {buildingId: lastSelectedBuildingId} = lastSelectedWorkspaceReservationLocation;

    const allBuildings = buildings
      .filter((building) => building.capacity > 0)
      .map((building): LocationNode => {
        const {occupation, isClosed, isFull, hasInsufficientSpaceForGroupBooking} =
          allNodeInfos[building.id] ?? nodeNotFoundResult;

        const allowWorkdayWithoutWorkspace = building.allowWorkdayWithoutWorkspace;
        const disabledDueToQuota =
          !workspaceReservation &&
          !allowWorkdayWithoutWorkspace &&
          (isSelectedDateFuture || quotaReached || !isDayAllowed);

        const disabledDueToOccupancy = !allowWorkdayWithoutWorkspace && isFull;

        const isOfficeDisabled =
          isClosed || disabledDueToQuota || disabledDueToOccupancy || hasInsufficientSpaceForGroupBooking;

        return {
          id: building.id,
          name: building.name,
          capacity: building.capacity,
          type: 'OfficeDay',
          nodeAvailability: isOfficeDisabled ? 'Unavailable' : 'Available',
          allowWorkdayWithoutWorkspace,
          occupation,
          locationAvailabilityInfo: {
            isClosed,
            isFull,
            isSelectedDateFuture,
            quotaReached,
            isDayAllowed,
            hasInsufficientSpaceForGroupBooking,
          },
        };
      });

    const workdayStatus = [
      {
        name: 'WorkingRemotely',
        capacity: 0,
        type: 'WorkingRemotely',
        nodeAvailability: 'Available',
        allowWorkdayWithoutWorkspace: true,
      } as LocationNode,
      {
        name: 'NotWorking',
        capacity: 0,
        type: 'NotWorking',
        nodeAvailability: 'Available',
        allowWorkdayWithoutWorkspace: true,
      } as LocationNode,
    ];

    const sortingOrder = [
      defaultBuildingId,
      lastSelectedBuildingId,
      'NotWorking',
      'WorkingRemotely',
      isToday ? checkInBuildingId : '',
    ];

    const sortedWorkdays = allBuildings
      .concat(workdayStatus)
      .sort((a, b) => compareWorkdayBySortingOrder(sortingOrder, a, b));

    return sortedWorkdays;
  },
)((_state, date) => date.toISOString());

export const getListOfSelectableOfficeNodesByType = createSelector(getSelectableOfficeNodes, (selectableOfficeNodes) =>
  groupSelectableOfficeNodesByType(selectableOfficeNodes),
);

export const getIsLastNodeSelected = createSelector(
  getBuildingEntities,
  getFloorEntities,
  getSelectedBuildingNodeId,
  getSelectedFloorNodeId,
  getSelectedAreaNodeId,
  (buildings, floors, selectedBuildingId, selectedFloorId, selectedAreaId): boolean => {
    const building = buildings[selectedBuildingId];
    const floor = floors[selectedFloorId];

    if (building && (building.floors.length === 0 || (floor && (floor.areas.length === 0 || selectedAreaId)))) {
      return true;
    }
    return false;
  },
);

export const getCanSaveWorkspaceReservation = createCachedSelector(
  getIsSavingRegistration,
  getIsDeletingRegistration,
  getIsLoadingDeskSuggestions,
  getIsLoadingDeskBookingStatus,
  getSuggestionError,
  getNodeInformationByNodeIdInEditMode,
  getIsLoadingParkingLots,
  getIsLoadingParkingCount,
  getIsLastNodeSelected,
  (
    isRegistering,
    isDeleting,
    isLoadingDeskSuggestions,
    isLoadingDeskBookingStatus,
    suggestionError,
    {isClosed, isFull},
    isLoadingParkingLots,
    isLoadingParkingCount,
    isLastNodeSelected,
  ) => {
    if (
      !isRegistering &&
      !isDeleting &&
      !isLoadingDeskSuggestions &&
      !isLoadingDeskBookingStatus &&
      !isFull &&
      !isClosed &&
      !suggestionError &&
      !isLoadingParkingLots &&
      !isLoadingParkingCount &&
      isLastNodeSelected
    ) {
      return true;
    }
    return false;
  },
)((_state, {nodeId, date}) => `${nodeId}:${date?.toISOString() || ''}`);

export const getCanChangeWorkspaceReservation = createCachedSelector(
  getIsSavingRegistration,
  getIsDeletingRegistration,
  getTotalBuildings,
  (_state: RootState, date: string) => date,
  (isRegistering, isDeleting, buildingCount, dateISOString) => {
    if (!dateISOString || isRegistering || isDeleting || buildingCount <= 0) {
      return false;
    }
    return true;
  },
)((_state: RootState, date: string) => date || '');

export const getCanDeleteWorkspaceReservation = createCachedSelector(
  getStartOfTodayISOString,
  getSettings,
  getIsCreatingWorkspaceReservation,
  getIsUpdatingWorkspaceReservationById,
  getIsDeletingWorkspaceReservationById,
  getWorkspaceReservationById,
  (_state: RootState, date: string) => date,
  (todayISOString, settings, isCreating, isUpdating, isDeleting, workspaceReservation, dateISOString) => {
    if (!todayISOString || !dateISOString) return false;

    const today = parseISO(todayISOString);
    const date = parseISO(dateISOString);
    const isToday = workspaceReservation
      ? isSameDay(today, parseISO(workspaceReservation.startDate))
      : isSameDay(date, today);

    const isDayInPast = workspaceReservation && isBeforeToday(parseISO(workspaceReservation.startDate), today);

    return (
      (!isToday || (isToday && settings.registrationPolicy === 'SameDay')) &&
      !isCreating &&
      !isUpdating &&
      !isDeleting &&
      !isDayInPast
    );
  },
)((_state: RootState, workspaceReservationId: string) => workspaceReservationId);

export const getSelectedBuildingName = createSelector(
  (state: RootState) => getBuildingById(state, getSelectedBuildingNodeId(state)),
  (building) => building?.name ?? '',
);

export const getLocationNameByWorkspaceReservationId = createCachedSelector(
  (state: RootState, workspaceReservationId: string) => getWorkspaceReservationById(state, workspaceReservationId),
  (workspaceReservation) => {
    if (!workspaceReservation) {
      return '';
    }

    if (workspaceReservation.buildingId === workspaceReservation.nodeId) {
      return workspaceReservation.buildingName;
    }
    return [workspaceReservation.floorName, workspaceReservation.areaName]
      .filter((text) => text && text !== '')
      .join(', ');
  },
)((_state: RootState, workspaceReservationId: string) => workspaceReservationId);

export const getSelectedSpaceNodeId = createSelector(
  getSelectedBuildingNodeId,
  getSelectedFloorNodeId,
  getSelectedAreaNodeId,
  (selectedBuildingId, selectedFloorId, selectedAreaId) => {
    if (selectedAreaId) {
      return selectedAreaId;
    }
    if (selectedFloorId) {
      return selectedFloorId;
    }
    if (selectedBuildingId) {
      return selectedBuildingId;
    }
    return '';
  },
);

const selectConnectionIdsOnNode = (workspaceReservationsForConnections: WorkspaceReservation[], nodeId: string) => {
  // When the workspace reservation was booked under a desk, we want to match connections based on the area
  return workspaceReservationsForConnections
    .filter((s) => s.nodeId === nodeId || (!!s.deskId && s.areaId === nodeId))
    .map((s) => s.userId);
};

const selectBuildingEntitiesAsArray = (buildingEntities: Dictionary<Building>, buildingId?: string) => {
  if (!!buildingId && buildingEntities[buildingId]) {
    return [buildingEntities[buildingId]] as Building[];
  }
  return Object.values(buildingEntities) as Building[];
};

export const getAllBuildingLocations = createCachedSelector(
  getBuildingEntities,
  getFloorEntities,
  getAreaEntities,
  (state: RootState, {date}: GetBuildingLocationsProps) => getWorkspaceReservationsForConnectionsByDate(state, {date}),
  (_state: RootState, props: GetBuildingLocationsProps) => props,
  (
    buildingEntities,
    floorEntities,
    areaEntities,
    workspaceReservationsForConnections,
    {buildingId, userNodeId, onlyWithConnections},
  ): BuildingLocation[] => {
    return selectBuildingEntitiesAsArray(buildingEntities, buildingId)
      .flatMap((building): BuildingLocation[] => {
        if (building.floors.length === 0) {
          // If the building has no floors or areas, we return the one building
          return [
            {
              buildingId: building.id,
              buildingName: building.name,
              floorId: '',
              floorNumber: 0,
              areaId: '',
              areaName: '',
              nodeId: building.id,
              name: building.name,
              userHasAccess: true,
              isUserLocation: userNodeId === building.id,
              connectionIds: selectConnectionIdsOnNode(workspaceReservationsForConnections, building.id),
            },
          ];
        }

        const floorsInBuilding = building.floors.map((floorId) => floorEntities[floorId] as Floor);
        const areasInBuilding = floorsInBuilding.flatMap((f) => f.areas).map((areaId) => areaEntities[areaId] as Area);

        return areasInBuilding
          .map((area): BuildingLocation => {
            const floor = floorEntities[area.floorId] as Floor;
            return {
              buildingId: building.id,
              buildingName: building.name,
              floorId: area.floorId,
              floorNumber: floor.number,
              areaId: area.id,
              areaName: area.name,
              nodeId: area.id,
              name: `${floor.name}, ${area.name}`,
              userHasAccess: true,
              isUserLocation: userNodeId === area.id,
              connectionIds: selectConnectionIdsOnNode(workspaceReservationsForConnections, area.id),
            };
          })
          .concat(
            floorsInBuilding
              .filter((f) => f.areas.length === 0)
              .map(
                (floor): BuildingLocation => ({
                  buildingId: floor.buildingId,
                  buildingName: building.name,
                  floorId: floor.id,
                  floorNumber: floor.number,
                  areaId: '',
                  areaName: '',
                  nodeId: floor.id,
                  name: floor.name,
                  userHasAccess: true,
                  isUserLocation: userNodeId === floor.id,
                  connectionIds: selectConnectionIdsOnNode(workspaceReservationsForConnections, floor.id),
                }),
              ),
          );
      })
      .filter((node) => !onlyWithConnections || (onlyWithConnections && node.connectionIds.length > 0))
      .sort((a, b) => {
        if (a.isUserLocation && !b.isUserLocation) {
          return -1;
        }
        if (!a.isUserLocation && b.isUserLocation) {
          return 1;
        }

        if (a.floorNumber !== b.floorNumber) {
          return numericCompareAsc(a.floorNumber, b.floorNumber);
        }

        return stringCompare(a.areaName, b.areaName);
      });
  },
)(
  (_state: RootState, {date, buildingId, userNodeId, onlyWithConnections}: GetBuildingLocationsProps) =>
    `${date}:${buildingId}:${userNodeId}:${onlyWithConnections}`,
);

export const getBuildingHasOnlyOneNodeAndBookableDesks = createSelector(
  (state: RootState) => getBuildingById(state, getSelectedBuildingNodeId(state)),
  (state: RootState) => getFloorById(state, getSelectedFloorNodeId(state)),
  (state: RootState) => getAreaById(state, getSelectedAreaNodeId(state)),
  (building, floor, area) => {
    return building?.floors.length === 1 && floor?.areas.length === 1 && area?.hasBookableDesks;
  },
);
